/**
 * @file trail.schema.ts
 * @description Define os schemas Zod para as entidades 'Trail' e 'TrailEnrollment'.
 * Trilhas são conjuntos ordenados de vídeos (atualmente restritos a subtype=COURSE)
 * que o usuário deve completar para obter um certificado de conclusão.
 *
 * Renomeações aplicadas em relação ao formato legado (aceitas via preprocess):
 *   courses[].courseId           → videos[].videoId
 *   courses                      → videos
 *   courseProgress[].courseId    → videoProgress[].videoId
 *   courseProgress               → videoProgress
 */

import { z } from 'zod';

/**
 * Fases da jornada — dimensão vertical.
 * POSTPARTUM é derivado do status da gestação (pregnancy.status === 'completed').
 */
export const TrailStage = z.enum(['PREGNANT', 'TENTANTE', 'POSTPARTUM', 'MAE_DE_ANJO', 'ADOTANTE', 'MAE']);

/**
 * Tipo de audiência — dimensão horizontal.
 * Determina se a trilha é para a mãe ou para a figura paterna.
 */
export const TrailRole = z.enum(['MOTHER', 'FATHER_FIGURE']);

export const TrailVisibility = z.enum(['VISIBLE', 'HIDDEN']);
export const TrailEnrollmentStatus = z.enum(['active', 'completed', 'abandoned']);
export const TrailVideoStatus = z.enum(['not_started', 'in_progress', 'completed']);
/** @deprecated Use `TrailVideoStatus`. */
export const TrailCourseStatus = TrailVideoStatus;


// --- Sub-Schemas ---

/**
 * Referência a um vídeo dentro de uma trilha.
 * Atualmente, apenas vídeos com `subtype: 'COURSE'` são aceitos — a validação
 * desta invariante ocorre no controller (não no schema), pois requer acesso
 * ao documento do vídeo referenciado.
 */
const TrailVideoSchema = z.object({
  videoId: z.string(),
  order: z.number().int(),
  isRequired: z.boolean().default(true),
});

/**
 * Condições opcionais de visibilidade da trilha.
 * Determinam quando a trilha aparece para o usuário baseado em dados da gestação.
 */
const TrailConditionsSchema = z.object({
  gestationalWeekRange: z.object({
    min: z.number().int().min(1).max(42).optional(),
    max: z.number().int().min(1).max(42).optional(),
  }).optional(),
  trimester: z.array(z.enum(['1', '2', '3'])).optional(),
  postpartumWeekRange: z.object({
    min: z.number().int().min(0).max(52).optional(),
    max: z.number().int().min(0).max(52).optional(),
  }).optional(),
});

/**
 * Configuração de certificado da trilha.
 */
const TrailCertificateConfigSchema = z.object({
  enabled: z.boolean().default(true),
  templateDocxUrl: z.string().optional(),
  backgroundImageUrl: z.string().optional(),
  tags: z.array(z.object({
    key: z.string(),
    x: z.number().min(0).max(100),
    y: z.number().min(0).max(100),
    /** Pixels da imagem de fundo, não pontos. Ver o teto em trail.controller.ts. */
    fontSize: z.number().min(8).max(1200, 'Tamanho da fonte do certificado fora do limite').default(24),
    color: z.string().default('#000000'),
    fontWeight: z.enum(['normal', 'bold']).default('normal'),
  })).optional(),
});


// --- Main Trail Schema ---

const TrailSchemaBase = z.object({
  id: z.string(),
  title: z.string().min(1),
  description: z.string().min(1),
  coverUrl: z.string().min(1),
  partnerId: z.string().nullable(),
  videos: z.array(TrailVideoSchema),
  targetAudience: z.object({
    stages: z.array(TrailStage),
    roles: z.array(TrailRole),
  }),
  conditions: TrailConditionsSchema.optional(),
  certificate: TrailCertificateConfigSchema.optional(),
  visibility: TrailVisibility.default('HIDDEN'),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
});

/**
 * Preprocessor para trail docs legados (com `courses` em vez de `videos`).
 */
const TrailSchema = z.preprocess(
  (raw) => {
    if (typeof raw !== 'object' || raw === null) return raw;
    const v = raw as Record<string, unknown>;
    let videos = v.videos as unknown;
    if (videos === undefined && Array.isArray(v.courses)) {
      videos = (v.courses as Array<Record<string, unknown>>).map((item) => ({
        ...item,
        videoId: item.videoId ?? item.courseId,
      }));
    }
    return { ...v, videos };
  },
  TrailSchemaBase
);


// --- Trail Video Progress (enrollment) ---

/**
 * Progresso de um vídeo individual dentro de uma inscrição de trilha.
 *
 * Os nomes `watchedClasses`/`totalClasses`/`watchedVideoIds` foram preservados
 * porque trilhas só suportam cursos — esses campos refletem aulas (classes)
 * concluídas, o que é semântica específica de COURSE. Renomeação para
 * `watchedItems`/`totalItems` virá apenas se trilhas passarem a suportar
 * outros subtipos de vídeo (planejado para Fase 4 se necessário).
 */
const TrailVideoProgressSchema = z.object({
  videoId: z.string(),
  status: TrailVideoStatus.default('not_started'),
  completedAt: z.date().nullable(),
  watchedClasses: z.number().int().default(0),
  totalClasses: z.number().int(),
  watchedVideoIds: z.array(z.string()).default([]),
});

// Note: TrailCourseProgressSchema deprecated alias is re-exported below.


// --- Trail Enrollment Schema ---

const TrailEnrollmentSchemaBase = z.object({
  id: z.string(),
  trailId: z.string(),
  userId: z.string(),
  pregnancyId: z.string().nullable(),
  partnerId: z.string().nullable(),
  videoProgress: z.array(TrailVideoProgressSchema),
  status: TrailEnrollmentStatus.default('active'),
  overallProgress: z.number().min(0).max(100).default(0),
  certificateIssuedAt: z.date().nullable(),
  certificateUrl: z.string().nullable(),
  enrolledAt: z.date(),
  completedAt: z.date().nullable(),
  updatedAt: z.date(),
});

/**
 * Preprocessor para enrollment docs legados (com `courseProgress` em vez de `videoProgress`).
 * Os campos internos (watchedClasses/totalClasses/watchedVideoIds) permanecem inalterados.
 */
const TrailEnrollmentSchema = z.preprocess(
  (raw) => {
    if (typeof raw !== 'object' || raw === null) return raw;
    const v = raw as Record<string, unknown>;
    let videoProgress = v.videoProgress as unknown;
    if (videoProgress === undefined && Array.isArray(v.courseProgress)) {
      videoProgress = (v.courseProgress as Array<Record<string, unknown>>).map((item) => ({
        ...item,
        videoId: item.videoId ?? item.courseId,
      }));
    }
    return { ...v, videoProgress };
  },
  TrailEnrollmentSchemaBase
);


// --- Inferred TypeScript Types ---

type Trail = z.infer<typeof TrailSchema>;
type TrailEnrollment = z.infer<typeof TrailEnrollmentSchema>;
type TrailVideo = z.infer<typeof TrailVideoSchema>;
/** @deprecated Use `TrailVideo`. */
type TrailCourse = TrailVideo;
type TrailConditions = z.infer<typeof TrailConditionsSchema>;
type TrailCertificateConfig = z.infer<typeof TrailCertificateConfigSchema>;
type TrailVideoProgress = z.infer<typeof TrailVideoProgressSchema>;
/** @deprecated Use `TrailVideoProgress`. */
type TrailCourseProgress = TrailVideoProgress;
type TrailStageType = z.infer<typeof TrailStage>;
type TrailRoleType = z.infer<typeof TrailRole>;
type TrailVisibilityType = z.infer<typeof TrailVisibility>;
type TrailEnrollmentStatusType = z.infer<typeof TrailEnrollmentStatus>;
type TrailVideoStatusType = z.infer<typeof TrailVideoStatus>;
/** @deprecated Use `TrailVideoStatusType`. */
type TrailCourseStatusType = TrailVideoStatusType;


// --- Public Exports ---

export {
  TrailSchema,
  TrailEnrollmentSchema,
  TrailVideoSchema,
  TrailConditionsSchema,
  TrailCertificateConfigSchema,
  TrailVideoProgressSchema,
  // Deprecated aliases
  TrailVideoSchema as TrailCourseSchema,
  TrailVideoProgressSchema as TrailCourseProgressSchema,
};

export type {
  Trail,
  TrailEnrollment,
  TrailVideo,
  TrailCourse,
  TrailConditions,
  TrailCertificateConfig,
  TrailVideoProgress,
  TrailCourseProgress,
  TrailStageType,
  TrailRoleType,
  TrailVisibilityType,
  TrailEnrollmentStatusType,
  TrailVideoStatusType,
  TrailCourseStatusType,
};
